import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { ACCENT } from "../artistStudio/shared";
import { INK, MUTED } from "../fanHome/libraryParts";
import { money, apiErrorMessage } from "../fanMembership/fanMembershipApi";
import { receiptLink } from "../receipts/receiptApi";
import { fanTipApi } from "./fanTipApi";

// "Fund <artist>": a one-off tip from the artist's page, like the Fund sheet in the mobile design. The fan picks an amount (or
// types one) and may add a message. The server returns the fee and what the artist receives; the fan is charged exactly the tip
// amount, and the tip counts only after the server has checked the payment with Stripe. Test environments also get Simulate buttons.
const ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "16px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" },
    invalid: { color: "#BE1F2E" },
  },
};

const Line = ({ label, value, strong }) => (
  <div className={`flex justify-between py-1 text-sm ${strong ? "font-bold" : ""}`} style={{ color: strong ? INK : MUTED }}>
    <span>{label}</span><span>{value}</span>
  </div>
);

export default function FundArtistSheet({ artist, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [amount, setAmount] = useState(10);
  const [other, setOther] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [busy, setBusy] = useState("");
  const [failure, setFailure] = useState("");
  const [done, setDone] = useState(null);
  const busyRef = useRef(false);

  useEffect(() => {
    let live = true;
    fanTipApi.preview(artist.slug)
      .then((d) => live && setInfo(d))
      .catch((e) => live && setLoadError(apiErrorMessage(e, "This page could not be loaded. Please try again.")));
    return () => { live = false; };
  }, [artist.slug]);

  const chosen = other !== "" ? Number(other) : amount;
  useEffect(() => {
    if (!info) return undefined;
    if (!(chosen > 0)) { setQuote(null); setQuoteError(""); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      fanTipApi.preview(artist.slug, chosen)
        .then((d) => { if (live) { setQuote(d.quote); setQuoteError(""); } })
        .catch((e) => { if (live) { setQuote(null); setQuoteError(apiErrorMessage(e, "Enter a valid amount.")); } });
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [chosen, info, artist.slug]);

  const begin = () => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setFailure("");
    return true;
  };
  const end = () => { busyRef.current = false; setBusy(""); };
  const body = () => ({ artist_slug: artist.slug, amount: chosen, ...(message.trim() ? { message: message.trim() } : {}) });

  const confirmWithServer = async (intentId, stripeMessage) => {
    try {
      const result = await fanTipApi.confirm({ payment_intent_id: intentId });
      if (result.status === "succeeded") { setDone(result.tip); return; }
      setFailure(result.message || "Your payment is still being processed. Please check Purchase History in a moment.");
    } catch (e) {
      setFailure(stripeMessage ? `${stripeMessage} You have not been charged.` : apiErrorMessage(e, "We could not confirm your payment. Please try again."));
    }
  };

  const pay = async (event) => {
    event.preventDefault();
    if (!quote || !chosen) return;
    if (!stripe || !elements) { setFailure("The payment form is still loading. Please try again in a moment."); return; }
    if (!name.trim()) { setFailure("Enter the name on the card."); return; }
    if (!begin()) return;
    setBusy("pay");
    try {
      let started;
      try {
        started = await fanTipApi.start(body());
      } catch (e) {
        setFailure(apiErrorMessage(e, "The payment could not be started. Please try again."));
        return;
      }
      const { error, paymentIntent } = await stripe.confirmCardPayment(started.client_secret, {
        payment_method: { card: elements.getElement(CardNumberElement), billing_details: { name: name.trim() } },
      });
      await confirmWithServer(started.payment_intent_id, error ? error.message : paymentIntent && paymentIntent.status !== "succeeded" ? "The payment was not completed." : "");
    } finally {
      end();
    }
  };

  const simulate = async (outcome) => {
    if (!quote || !chosen || !begin()) return;
    setBusy(outcome);
    try {
      const result = await fanTipApi.simulate({ ...body(), outcome });
      setDone(result.tip);
    } catch (e) {
      setFailure(apiErrorMessage(e, "The simulated payment could not be processed."));
    } finally {
      end();
    }
  };

  const shell = (children) => (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-center md:px-4" role="dialog" aria-modal="true" aria-label={`Fund ${artist.name}`}>
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 shadow-xl md:rounded-3xl">{children}</div>
    </div>
  );

  if (done) {
    return shell(
      <div className="py-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#e3f1e7]" aria-hidden="true">
          <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </div>
        <h2 className="mt-5 text-2xl font-bold" style={{ color: INK }}>Tip sent to {artist.name}</h2>
        <p className="mt-2 text-sm" style={{ color: MUTED }}>
          {money((quote && quote.total) || done.tip, done.currency)} was charged{done.payment_mode === "simulated" ? " (test payment)" : " to your card"}. {artist.name} receives {money(done.artist_receives, done.currency)}.
        </p>
        <button type="button" onClick={onClose} className="mt-6 w-full rounded-full px-5 py-3 text-[15px] font-semibold text-white" style={{ backgroundColor: ACCENT }}>Back to artist</button>
        <Link to={receiptLink("tip", done.id)} className="mt-3 block w-full rounded-full border border-[#bcd8c4] bg-[#e9f4ec] px-5 py-3 text-[15px] font-semibold" style={{ color: ACCENT }}>View receipt</Link>
      </div>
    );
  }

  const busyAny = Boolean(busy);
  return shell(
    <>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-gray-500">×</button>
        <h2 className="text-lg font-bold" style={{ color: INK }}>Fund {artist.name}</h2>
        <span className="w-4" />
      </div>
      {loadError && <p className="mt-6 text-sm text-red-600" role="alert">{loadError}</p>}
      {!info && !loadError && <p className="mt-6 text-sm" style={{ color: MUTED }}>Loading…</p>}
      {info && (
        <form onSubmit={pay} className="mt-5">
          <p className="text-sm font-semibold" style={{ color: INK }}>Choose an amount</p>
          <div className="mt-2 grid grid-cols-4 gap-2" role="group" aria-label="Amount">
            {info.presets.map((p) => {
              const active = other === "" && amount === p;
              return (
                <button key={p} type="button" aria-pressed={active} onClick={() => { setAmount(p); setOther(""); }}
                  className={`rounded-2xl py-2.5 text-[15px] font-semibold ${active ? "text-white" : "bg-[#e9f4ec]"}`}
                  style={active ? { backgroundColor: ACCENT } : { color: ACCENT }}>${p}</button>
              );
            })}
          </div>
          <label htmlFor="tip-other" className="mt-4 block text-sm" style={{ color: MUTED }}>Other amount</label>
          <input id="tip-other" inputMode="decimal" value={other} placeholder="$ 0" onChange={(e) => setOther(e.target.value.replace(/[^0-9.]/g, "").slice(0, 7))}
            className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-[15px]" />
          <label htmlFor="tip-message" className="mt-4 block text-sm" style={{ color: MUTED }}>Message <span className="text-gray-400">(optional)</span></label>
          <textarea id="tip-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={info.message_max} rows={3} placeholder={`Say something to ${artist.name}…`}
            className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-[15px]" />

          <div className="mt-4 rounded-2xl bg-[#f4f4f2] px-4 py-3" aria-live="polite">
            {quote ? (
              <>
                <Line label="Tip" value={money(quote.tip, quote.currency)} />
                <Line label={quote.fee_label || "Processing fee"} value={money(quote.processing_fee, quote.currency)} />
                <Line label={`${artist.name} receives`} value={money(quote.artist_receives, quote.currency)} strong />
                {quote.platform_charge > 0 && <Line label="Platform charge (added)" value={money(quote.platform_charge, quote.currency)} />}
                {quote.platform_charge > 0 && <Line label="You pay" value={money(quote.total, quote.currency)} strong />}
              </>
            ) : (
              <p className="text-sm" style={{ color: quoteError ? "#BE1F2E" : MUTED }}>{quoteError || "Choose an amount to see the fee."}</p>
            )}
          </div>

          <p className="mt-5 text-sm font-semibold" style={{ color: INK }}>Pay by card</p>
          <label className="mt-2 block text-sm" style={{ color: MUTED }}>Name on card</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="cc-name" className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-[15px]" />
          <label className="mt-3 block text-sm" style={{ color: MUTED }}>Card number</label>
          <div className="mt-1 rounded-2xl border border-gray-200 px-4 py-3"><CardNumberElement options={ELEMENT_OPTIONS} /></div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm" style={{ color: MUTED }}>Expiry date</label>
              <div className="mt-1 rounded-2xl border border-gray-200 px-4 py-3"><CardExpiryElement options={ELEMENT_OPTIONS} /></div>
            </div>
            <div>
              <label className="block text-sm" style={{ color: MUTED }}>CVC</label>
              <div className="mt-1 rounded-2xl border border-gray-200 px-4 py-3"><CardCvcElement options={ELEMENT_OPTIONS} /></div>
            </div>
          </div>

          {failure && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">{failure}</p>}
          <button type="submit" disabled={busyAny || !quote || !stripe} className="mt-5 w-full rounded-full px-5 py-3.5 text-[15px] font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>
            {busy === "pay" ? "Sending…" : quote ? `Send ${money(quote.total || quote.tip, quote.currency)}` : "Send"}
          </button>
          <p className="mt-2 text-center text-xs text-gray-500">Your card is processed securely by Stripe. The tip counts only after the payment is confirmed.</p>
          {info.simulation_available && (
            <div className="mt-4 rounded-2xl border border-dashed border-gray-300 p-3">
              <p className="text-xs font-semibold text-gray-600">Test only (never shown in production)</p>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => simulate("success")} disabled={busyAny || !quote} className="rounded-full border-2 px-4 py-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: ACCENT, color: ACCENT }}>{busy === "success" ? "Working…" : "Simulate success"}</button>
                <button type="button" onClick={() => simulate("declined")} disabled={busyAny || !quote} className="rounded-full border-2 border-red-300 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-60">{busy === "declined" ? "Working…" : "Simulate declined"}</button>
              </div>
            </div>
          )}
        </form>
      )}
    </>
  );
}
